import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ArrowLeft, Save, Trash2, Send, Instagram, Plus, X, Megaphone, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import ShareFlyerToSocial from "@/components/ShareFlyerToSocial";
import { ImageUploadField, ImageGalleryField } from "@/components/ImageUploadField";

const blank = {
  title: "", slug: "", subtitle: "", description: "",
  location_name: "", location_address: "", location_map_url: "",
  starts_at: "", ends_at: "",
  price_cents: 0, capacity: "",
  cover_image: "", gallery: [], tags: [], keywords: [],
  published: true,
};

function isoToLocalInput(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const off = d.getTimezoneOffset();
    const local = new Date(d.getTime() - off * 60000);
    return local.toISOString().slice(0, 16);
  } catch { return ""; }
}

function localInputToIso(s) {
  if (!s) return null;
  try { return new Date(s).toISOString(); } catch { return null; }
}

export default function MeetupEdit() {
  const { meetup_id } = useParams();
  const navigate = useNavigate();
  const isNew = meetup_id === "new" || !meetup_id;

  const [form, setForm] = useState(blank);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [registrations, setRegistrations] = useState([]);
  const [showBroadcast, setShowBroadcast] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [broadcastForm, setBroadcastForm] = useState({ subject: "", headline: "", message: "", cta_label: "", cta_url: "" });
  const [broadcasting, setBroadcasting] = useState(false);
  const [priceDollars, setPriceDollars] = useState("0");

  useEffect(() => {
    if (isNew) { setLoading(false); return; }
    (async () => {
      try {
        const { data } = await api.get(`/admin/meetups/${meetup_id}`);
        setForm({
          ...blank, ...data,
          starts_at: data.starts_at || "",
          ends_at: data.ends_at || "",
          capacity: data.capacity ?? "",
          gallery: data.gallery || [],
          tags: data.tags || [],
          keywords: data.keywords || [],
        });
        setPriceDollars(((data.price_cents || 0) / 100).toFixed(2));
        const regs = await api.get(`/admin/meetups/${meetup_id}/registrations`);
        setRegistrations(regs.data || []);
      } catch {
        toast.error("Failed to load meetup");
      } finally {
        setLoading(false);
      }
    })();
  }, [meetup_id, isNew]);

  const save = async () => {
    if (!form.title || !form.description || !form.cover_image) {
      toast.error("Title, cover image, and description are required. Leave date/location blank for TBA.");
      return;
    }
    setSaving(true);
    const price_cents = Math.max(0, Math.round(parseFloat(priceDollars || "0") * 100));
    const capacityVal = form.capacity === "" || form.capacity == null ? null : parseInt(form.capacity, 10);
    const payload = {
      ...form,
      price_cents,
      capacity: capacityVal,
      starts_at: form.starts_at ? (form.starts_at.includes("T") && form.starts_at.length <= 16 ? localInputToIso(form.starts_at) : form.starts_at) : null,
      ends_at: form.ends_at ? (form.ends_at.includes("T") && form.ends_at.length <= 16 ? localInputToIso(form.ends_at) : form.ends_at) : null,
    };
    try {
      if (isNew) {
        const { data } = await api.post("/admin/meetups", payload);
        toast.success("Meetup created");
        navigate(`/admin/meetups/${data.id}`);
      } else {
        await api.patch(`/admin/meetups/${meetup_id}`, payload);
        toast.success("Saved");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Delete this meetup? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/meetups/${meetup_id}`);
      toast.success("Deleted");
      navigate("/admin/meetups");
    } catch {
      toast.error("Failed to delete");
    }
  };

  const broadcast = async () => {
    if (!broadcastForm.subject || !broadcastForm.headline || !broadcastForm.message) {
      toast.error("Subject, headline, and message are required");
      return;
    }
    if (!window.confirm(`Send to ${registrations.length} registrant(s)?`)) return;
    setBroadcasting(true);
    try {
      const { data } = await api.post(`/admin/meetups/${meetup_id}/broadcast`, broadcastForm);
      toast.success(`Sent to ${data.sent} of ${data.recipients} registrants`);
      setShowBroadcast(false);
      setBroadcastForm({ subject: "", headline: "", message: "", cta_label: "", cta_url: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Broadcast failed");
    } finally {
      setBroadcasting(false);
    }
  };

  const addToList = (key, value) => {
    if (!value) return;
    setForm({ ...form, [key]: [...form[key], value] });
  };

  const removeFromList = (key, idx) => {
    const next = [...form[key]]; next.splice(idx, 1);
    setForm({ ...form, [key]: next });
  };

  if (loading) return <div className="p-10 text-zinc-500">Loading…</div>;

  return (
    <div className="p-8 max-w-7xl mx-auto" data-testid="admin-meetup-edit">
      <Link to="/admin/meetups" className="inline-flex items-center text-zinc-400 hover:text-white text-xs uppercase tracking-widest mb-6">
        <ArrowLeft className="h-3 w-3 mr-2" /> Back to meetups
      </Link>

      <div className="flex flex-wrap items-center justify-between gap-3 mb-8">
        <h1 className="font-display uppercase text-3xl md:text-4xl text-white">{isNew ? "New Meetup" : form.title || "Edit Meetup"}</h1>
        <div className="flex items-center gap-2 flex-wrap">
          {!isNew && (
            <>
              <Button variant="outline" onClick={() => setShowShare(true)} className="border-[#27272A] bg-[#0F0F0F] text-white hover:bg-[#1A1A1A] rounded-sm font-bold uppercase tracking-widest" data-testid="admin-meetup-share-btn">
                <Instagram className="h-4 w-4 mr-2" /> Share flyer
              </Button>
              <Button variant="outline" onClick={() => setShowBroadcast(true)} className="border-[#27272A] bg-[#0F0F0F] text-white hover:bg-[#1A1A1A] rounded-sm font-bold uppercase tracking-widest" data-testid="admin-meetup-broadcast-btn">
                <Megaphone className="h-4 w-4 mr-2" /> Email registrants
              </Button>
              <a href={`/meetups/${form.slug}`} target="_blank" rel="noreferrer" className="inline-flex items-center text-xs uppercase tracking-widest text-zinc-400 hover:text-white px-3 py-2">
                <ExternalLink className="h-4 w-4 mr-1" /> View live
              </a>
            </>
          )}
          <Button onClick={save} disabled={saving} className="bg-[#FF331F] hover:bg-[#FF5242] text-white font-bold uppercase tracking-widest rounded-sm" data-testid="admin-meetup-save">
            <Save className="h-4 w-4 mr-2" /> {saving ? "Saving…" : "Save"}
          </Button>
          {!isNew && (
            <Button variant="ghost" onClick={remove} className="text-red-500 hover:text-red-400 font-bold uppercase tracking-widest" data-testid="admin-meetup-delete">
              <Trash2 className="h-4 w-4 mr-2" /> Delete
            </Button>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        {/* Form */}
        <div className="lg:col-span-2 space-y-6">
          <Card title="Basics">
            <FormRow label="Title *">
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Saturday Park Boxing Session" data-testid="meetup-form-title" className="bg-[#050505] border-[#27272A] text-white rounded-sm" />
            </FormRow>
            <FormRow label="Subtitle / short tagline">
              <Input value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} placeholder="Beginners welcome · 90-min outdoor session" className="bg-[#050505] border-[#27272A] text-white rounded-sm" />
            </FormRow>
            <FormRow label="Slug (URL) — auto-generated if blank">
              <Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="saturday-park-boxing-session" className="bg-[#050505] border-[#27272A] text-white rounded-sm font-mono text-xs" />
            </FormRow>
            <FormRow label="Description *">
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={7} placeholder="What's the event about? What will folks do? Who's it for? Use blank lines to separate paragraphs." className="bg-[#050505] border-[#27272A] text-white rounded-sm" />
            </FormRow>
            <FormRow label="Cover image / flyer *">
              <ImageUploadField
                value={form.cover_image}
                onChange={(url) => setForm({ ...form, cover_image: url })}
                label=""
                testid="meetup-form-cover"
              />
              <p className="text-[10px] text-zinc-500 mt-2">This is the image used on the meetup page, list cards, and the social-share flyer generator. Drop a file or click to upload — it&apos;s auto-compressed and served from your own domain so it always loads.</p>
            </FormRow>
          </Card>

          <Card title="When & Where">
            <p className="text-[10px] text-zinc-500 -mt-1">Leave any field blank to show it as "TBA" publicly.</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <FormRow label="Starts at (blank = TBA)">
                <Input type="datetime-local" value={isoToLocalInput(form.starts_at)} onChange={(e) => setForm({ ...form, starts_at: e.target.value })} className="bg-[#050505] border-[#27272A] text-white rounded-sm" data-testid="meetup-form-starts" />
              </FormRow>
              <FormRow label="Ends at (optional)">
                <Input type="datetime-local" value={isoToLocalInput(form.ends_at)} onChange={(e) => setForm({ ...form, ends_at: e.target.value })} className="bg-[#050505] border-[#27272A] text-white rounded-sm" />
              </FormRow>
            </div>
            <FormRow label="Location name (blank = TBA)">
              <Input value={form.location_name} onChange={(e) => setForm({ ...form, location_name: e.target.value })} placeholder="Branch Brook Park — Cherry Blossom Field" className="bg-[#050505] border-[#27272A] text-white rounded-sm" data-testid="meetup-form-location-name" />
            </FormRow>
            <FormRow label="Full address (blank = TBA)">
              <Input value={form.location_address} onChange={(e) => setForm({ ...form, location_address: e.target.value })} placeholder="Branch Brook Park, Newark, NJ 07104" className="bg-[#050505] border-[#27272A] text-white rounded-sm" data-testid="meetup-form-location-address" />
            </FormRow>
            <FormRow label="Google Maps URL (optional)">
              <Input value={form.location_map_url} onChange={(e) => setForm({ ...form, location_map_url: e.target.value })} placeholder="https://maps.app.goo.gl/..." className="bg-[#050505] border-[#27272A] text-white rounded-sm font-mono text-xs" />
            </FormRow>
          </Card>

          <Card title="Pricing & Capacity">
            <div className="grid sm:grid-cols-2 gap-4">
              <FormRow label="Price (USD) — leave 0 for free">
                <div className="flex items-center gap-2">
                  <span className="text-zinc-500">$</span>
                  <Input type="number" min="0" step="0.01" value={priceDollars} onChange={(e) => setPriceDollars(e.target.value)} placeholder="0" className="bg-[#050505] border-[#27272A] text-white rounded-sm" data-testid="meetup-form-price" />
                </div>
              </FormRow>
              <FormRow label="Capacity (leave blank for unlimited)">
                <Input type="number" min="1" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} placeholder="20" className="bg-[#050505] border-[#27272A] text-white rounded-sm" data-testid="meetup-form-capacity" />
              </FormRow>
            </div>
          </Card>

          <Card title="Gallery (post-event photos)">
            <ImageGalleryField
              items={form.gallery}
              onChange={(arr) => setForm({ ...form, gallery: arr })}
              testid="meetup-form-gallery"
            />
            <p className="text-[10px] text-zinc-500">Add photos after the event to keep the page useful as a recap (great for SEO). Multi-select supported.</p>
          </Card>

          <Card title="Tags & SEO keywords">
            <FormRow label="Tags (shown on event page)">
              <ListField items={form.tags} onAdd={(v) => addToList("tags", v)} onRemove={(i) => removeFromList("tags", i)} placeholder="Free · Outdoor · Beginners welcome" data-testid="meetup-form-tags" />
            </FormRow>
            <FormRow label="SEO keywords (hidden — for search engines)">
              <ListField items={form.keywords} onAdd={(v) => addToList("keywords", v)} onRemove={(i) => removeFromList("keywords", i)} placeholder="free boxing class newark" data-testid="meetup-form-keywords" />
            </FormRow>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <Card title="Visibility">
            <div className="flex items-center justify-between">
              <Label className="text-zinc-300">Published</Label>
              <Switch checked={!!form.published} onCheckedChange={(c) => setForm({ ...form, published: c })} data-testid="meetup-form-published" />
            </div>
            <p className="text-[10px] text-zinc-500 mt-1">Drafts don&apos;t appear in /meetups, sitemap, or to public visitors.</p>
          </Card>

          {!isNew && (
            <Card title={`Registrations (${registrations.length})`}>
              {registrations.length === 0 ? (
                <p className="text-zinc-500 text-sm">No registrations yet.</p>
              ) : (
                <div className="max-h-[480px] overflow-y-auto space-y-2">
                  {registrations.map((r) => (
                    <div key={r.id} className="border border-[#27272A] bg-[#050505] p-3" data-testid={`reg-row-${r.id}`}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="font-bold text-white text-sm">{r.full_name}</div>
                        <span className={`text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 ${
                          r.payment_status === "paid" ? "text-emerald-300 bg-emerald-950/40 border border-emerald-800" :
                          r.payment_status === "free" ? "text-blue-300 bg-blue-950/40 border border-blue-800" :
                          "text-amber-300 bg-amber-950/40 border border-amber-800"}`}>{r.payment_status}</span>
                      </div>
                      <div className="text-xs text-zinc-400">{r.email}</div>
                      <div className="text-xs text-zinc-400">{r.phone} · age {r.age}</div>
                      {(r.instagram_handle || r.facebook_handle) && (
                        <div className="text-[10px] text-zinc-500 mt-1">
                          {r.instagram_handle && <span>IG: @{r.instagram_handle}</span>}
                          {r.facebook_handle && <span className="ml-2">FB: {r.facebook_handle}</span>}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </Card>
          )}
        </div>
      </div>

      {/* Broadcast dialog */}
      <Dialog open={showBroadcast} onOpenChange={setShowBroadcast}>
        <DialogContent className="bg-[#0F0F0F] border-[#27272A] text-white max-w-2xl" data-testid="broadcast-dialog">
          <DialogHeader>
            <DialogTitle className="font-display uppercase flex items-center gap-2"><Megaphone className="h-5 w-5 text-[#FF331F]" /> Email all registrants</DialogTitle>
            <DialogDescription className="text-zinc-500 text-xs uppercase tracking-widest">
              {registrations.length} active recipient(s) — typical use: cancellation, location change, reminder
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <FormRow label="Subject *">
              <Input value={broadcastForm.subject} onChange={(e) => setBroadcastForm({ ...broadcastForm, subject: e.target.value })} placeholder="Update: location changed to Riverside Park" className="bg-[#050505] border-[#27272A] text-white rounded-sm" data-testid="broadcast-subject" />
            </FormRow>
            <FormRow label="Headline (shown big in the email) *">
              <Input value={broadcastForm.headline} onChange={(e) => setBroadcastForm({ ...broadcastForm, headline: e.target.value })} placeholder="Heads up — new location" className="bg-[#050505] border-[#27272A] text-white rounded-sm" data-testid="broadcast-headline" />
            </FormRow>
            <FormRow label="Message body *">
              <Textarea value={broadcastForm.message} onChange={(e) => setBroadcastForm({ ...broadcastForm, message: e.target.value })} rows={6} placeholder="Hi everyone — quick update. Due to a permit conflict we&apos;re moving the meetup to Riverside Park, same time. New address: ..." className="bg-[#050505] border-[#27272A] text-white rounded-sm" data-testid="broadcast-message" />
            </FormRow>
            <div className="grid sm:grid-cols-2 gap-3">
              <FormRow label="CTA button label (optional)">
                <Input value={broadcastForm.cta_label} onChange={(e) => setBroadcastForm({ ...broadcastForm, cta_label: e.target.value })} placeholder="See new location" className="bg-[#050505] border-[#27272A] text-white rounded-sm" />
              </FormRow>
              <FormRow label="CTA button URL (optional)">
                <Input value={broadcastForm.cta_url} onChange={(e) => setBroadcastForm({ ...broadcastForm, cta_url: e.target.value })} placeholder="https://maps.app.goo.gl/..." className="bg-[#050505] border-[#27272A] text-white rounded-sm font-mono text-xs" />
              </FormRow>
            </div>
            <div className="flex items-center justify-end gap-2 border-t border-[#27272A] pt-4">
              <Button variant="ghost" onClick={() => setShowBroadcast(false)} className="text-zinc-400">Cancel</Button>
              <Button onClick={broadcast} disabled={broadcasting} className="bg-[#FF331F] hover:bg-[#FF5242] text-white font-bold uppercase tracking-widest rounded-sm" data-testid="broadcast-send">
                <Send className="h-4 w-4 mr-2" /> {broadcasting ? "Sending…" : `Send to ${registrations.length}`}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {!isNew && <ShareFlyerToSocial open={showShare} onOpenChange={setShowShare} meetup={form} />}
    </div>
  );
}

function Card({ title, children }) {
  return (
    <section className="border border-[#27272A] bg-[#0F0F0F] p-5 space-y-3">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">{title}</div>
      {children}
    </section>
  );
}

function FormRow({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function ListField({ items, onAdd, onRemove, placeholder, ...props }) {
  const [value, setValue] = useState("");
  const submit = () => {
    const v = value.trim();
    if (v) { onAdd(v); setValue(""); }
  };
  return (
    <div className="space-y-2" {...props}>
      <div className="flex gap-2">
        <Input value={value} onChange={(e) => setValue(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submit(); } }} placeholder={placeholder} className="bg-[#050505] border-[#27272A] text-white rounded-sm" />
        <Button type="button" onClick={submit} variant="outline" className="border-[#27272A] bg-[#050505] text-white hover:bg-[#1A1A1A] rounded-sm">
          <Plus className="h-4 w-4" />
        </Button>
      </div>
      {items.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {items.map((it, i) => (
            <span key={i} className="inline-flex items-center gap-1 text-xs text-zinc-300 bg-[#1A1A1A] border border-[#27272A] px-2 py-1 rounded-sm">
              {it}
              <button type="button" onClick={() => onRemove(i)} className="text-zinc-500 hover:text-red-500">
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
